import { createFileRoute } from '@tanstack/react-router'
import type { ReactElement } from 'react'
import { z } from 'zod'

import { MemoryHealthPanel } from '~/features/memory-health'
import { useUrlState } from '~/lib/query/use-url-state'

/**
 * `/admin/memory-health` — the User Memory Health surface (P1-T1).
 *
 * Renders the §10.3 metric cards for the L3 user-memory program: opt-in rate,
 * memories per user, extraction yield, wrong-memory rate, extraction DLQ depth
 * and cost per user, straight from `GET /api/v1/admin/memory/health`.
 *
 * The window is a URL search key so an operator can link a colleague to the
 * exact view. `window_days` is constrained to the windows the API accepts.
 */
const memoryHealthSearchSchema = z.object({
  window: z.coerce.number().int().refine((value) => [7, 30, 90].includes(value), {
    message: 'window must be 7, 30 or 90',
  }).default(30),
})
type MemoryHealthSearch = z.infer<typeof memoryHealthSearchSchema>

const URL_DEFAULTS: MemoryHealthSearch = { window: 30 }
const WINDOWS = [7, 30, 90] as const

export const Route = createFileRoute('/admin/memory-health')({
  validateSearch: memoryHealthSearchSchema,
  component: MemoryHealthRoute,
})

function MemoryHealthRoute(): ReactElement {
  const search = Route.useSearch()
  const [, setUrl] = useUrlState(memoryHealthSearchSchema, URL_DEFAULTS)

  return (
    <div className="ka-page ka-memory-health-page">
      <header className="ka-page-head">
        <div>
          <h1>User Memory Health</h1>
          <p>
            Opt-in, extraction and answer-quality signals for stored user
            memory. Every metric the API cannot compute yet says so instead of
            showing a zero.
          </p>
        </div>
        <label className="ka-field">
          <span>Window</span>
          <select
            value={search.window}
            onChange={(event) => setUrl({ window: Number(event.target.value) as 7 | 30 | 90 })}
          >
            {WINDOWS.map((days) => (
              <option key={days} value={days}>
                Last {days} days
              </option>
            ))}
          </select>
        </label>
      </header>
      <MemoryHealthPanel windowDays={search.window} />
    </div>
  )
}
